import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  MANDATORY,
  PurchaseOrderMandatoryAction,
  SetValidPOMasks,
  ResetMasksFetchAction,
} from '../../actions/purchase-order-actions/purchase-order.actions';
import { catchError, delay, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import {
  JOBSITE_SET,
  LOAD_SALES_AREA_SUCCESS,
  LoadJobsiteSalesAreaComplete,
} from '../../actions/jobsite-actions/jobsite.actions';
import { ActionTypes } from '@ordersnproducts/modules/order-builder/modules/location/actions/experiences-actions/experiences.actions';
import {
  Experience,
  PurchaseOrderMaskFormat,
  PurchaseOrderService,
  PurchaseOrderValidationResponseV8,
} from '@cemex-core/cmx-api';
import { purchaseOrderMasksConvert } from '@ordersnproducts/modules/shared/utils/purchase-order-masks-convert';
import {
  getJobsiteSalesArea,
  getPurchaseOrderValidationData,
  OrderBuilderState,
} from '@ordersnproducts/modules/order-builder/reducers';
import { UnicEffectsAvailableEnum } from '@ordersnproducts/modules/shared/modules/dynamic-effects/deletable-effect-sources.constant';
import { EffectsRegistrationService } from '@ordersnproducts/modules/shared/modules/dynamic-effects/effects-registration.service';
import { ORDER_EXPERIENCES } from '@ordersnproducts/modules/shared/constants/order-experiences';

@Injectable()
export class PurchaseOrderMandatoryEffects {
  JobsiteLoadSalesAreaSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_SALES_AREA_SUCCESS, ActionTypes.SET_EXPERIENCE),
      map((action) => {
        return action as any;
      }),
      filter(() => this.isEffectAvailable()),
      withLatestFrom(this.store.select(getJobsiteSalesArea)),
      map(([action, salesArea]): Action => {
        const purchaseOrderValidation = !!salesArea[0] ? salesArea[0].purchaseOrderValidation : false;
        if (action.type === LOAD_SALES_AREA_SUCCESS) {
          return new PurchaseOrderMandatoryAction(purchaseOrderValidation);
        }
        const experience: Experience = action.payload as Experience;
        if (experience.experienceCode === ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK) {
          return new PurchaseOrderMandatoryAction(false);
        }
        return new PurchaseOrderMandatoryAction(purchaseOrderValidation);
      }),
    ),
  );

  JobsiteSet$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(JOBSITE_SET),
      map((payload: LoadJobsiteSalesAreaComplete) => {
        return new ResetMasksFetchAction();
      }),
    ),
  );

  PurchaseOrderMandatory$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MANDATORY),
      switchMap((action: PurchaseOrderMandatoryAction) => {
        // i.e. "if validation is required"
        if (action.payload) {
          return this.store.select(getPurchaseOrderValidationData).pipe(
            filter((state) => !!state.jobsiteId && !!state.productLine),
            take(1),
            switchMap((state) => {
              const payload = { jobsiteId: state.jobsiteId, productLine: state.productLine };
              return this.purchaseOrderService.getPOMasks(payload).pipe(
                // retry mask loading in 5 seconds on error
                catchError((error) => of(new PurchaseOrderMandatoryAction(true)).pipe(delay(5000))),
                map((response: PurchaseOrderValidationResponseV8) => {
                  const rawFormats = [];
                  if (response.formats == undefined || response.formats.length === 0) {
                    return new SetValidPOMasks(purchaseOrderMasksConvert([]));
                  }
                  response.formats.forEach((f: PurchaseOrderMaskFormat) => {
                    rawFormats.push(f.format);
                  });
                  return new SetValidPOMasks(purchaseOrderMasksConvert(rawFormats));
                }),
              );
            }),
          );
        }

        return of(new SetValidPOMasks(purchaseOrderMasksConvert([])));
      }),
    ),
  );

  /**
   * Identifies if the effect is available to be executed
   * @returns {boolean}
   */
  private isEffectAvailable(): boolean {
    return this.effectRegister.isEffectAvailable(UnicEffectsAvailableEnum.PurchaseOrderMandatoryEffects);
  }

  constructor(
    private actions$: Actions,
    private store: Store<OrderBuilderState>,
    private purchaseOrderService: PurchaseOrderService,
    private effectRegister: EffectsRegistrationService,
  ) {}
}
